import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileSpreadsheet, ArrowLeft, ArrowRight, Check, Package, Users, Search, Ban } from "lucide-react";
import useCotacaoUser from "./useCotacaoUser";

export default function CotacaoNovaPage() {
  const nav = useNavigate();
  const { canWrite, isAuditor } = useCotacaoUser();
  const [step, setStep] = useState(1);
  const [titulo, setTitulo] = useState("");
  const [produtos, setProdutos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [selProdutos, setSelProdutos] = useState({}); // { produto_id: qtd }
  const [selFornec, setSelFornec] = useState({}); // { fornecedor_id: true }
  const [buscaProd, setBuscaProd] = useState("");
  const [buscaForn, setBuscaForn] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      cotAxios.get("/cotacao/produtos").then((r) => r.data),
      cotAxios.get("/cotacao/fornecedores").then((r) => r.data),
    ]).then(([p, f]) => { setProdutos(p); setFornecedores(f); })
      .catch(() => toast.error("Erro ao carregar dados"));
  }, []);

  const produtosSelArr = useMemo(
    () => Object.entries(selProdutos).filter(([, q]) => q > 0).map(([produto_id, qtd]) => ({ produto_id, qtd })),
    [selProdutos]
  );
  const fornecSelArr = useMemo(
    () => Object.keys(selFornec).filter((id) => selFornec[id]).map((fornecedor_id) => ({ fornecedor_id })),
    [selFornec]
  );

  const prodFiltered = produtos.filter((p) => {
    const q = buscaProd.toLowerCase().trim();
    if (!q) return true;
    return [p.codigo, p.nome, p.marca, p.referencia].some((v) => (v || "").toLowerCase().includes(q));
  });
  const fornFiltered = fornecedores.filter((f) => {
    const q = buscaForn.toLowerCase().trim();
    if (!q) return true;
    return [f.razao_social, f.nome_fantasia, f.cnpj, f.nome_vendedor].some((v) => (v || "").toLowerCase().includes(q));
  });

  const toggleProd = (id) => {
    setSelProdutos((s) => {
      const next = { ...s };
      if (next[id]) delete next[id]; else next[id] = 1;
      return next;
    });
  };
  const changeQtd = (id, valor) => {
    setSelProdutos((s) => ({ ...s, [id]: Number(valor) || 0 }));
  };
  const toggleForn = (id) => setSelFornec((s) => ({ ...s, [id]: !s[id] }));

  const criar = async () => {
    if (!produtosSelArr.length) return toast.error("Selecione ao menos 1 produto");
    if (!fornecSelArr.length) return toast.error("Selecione ao menos 1 fornecedor");
    setSaving(true);
    try {
      const { data } = await cotAxios.post("/cotacao/cotacoes", {
        titulo: titulo.trim() || null,
        produtos: produtosSelArr,
        fornecedores: fornecSelArr,
      });
      toast.success(`Cotação nº ${data.numero} criada!`);
      nav(`/cotacoes/dashboard/cotacoes/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar cotação");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="cot-nova-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/cotacoes/dashboard/cotacoes")} data-testid="btn-voltar">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
      </div>
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-1">
        <FileSpreadsheet className="w-6 h-6 text-blue-700" /> Nova Cotação
      </h1>
      <p className="text-sm text-slate-500 mb-6">Selecione os produtos, depois os fornecedores. No final geramos os links WhatsApp.</p>

      {!canWrite && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Ban className="w-5 h-5 text-amber-700 shrink-0" />
            <div className="text-sm text-amber-900">
              {isAuditor
                ? "Auditor tem apenas acesso de visualização e não pode criar cotações."
                : "Você não tem permissão para criar cotações."}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      {canWrite && (
      <>
      <div className="flex items-center gap-2 mb-6 text-sm">
        {[
          { n: 1, l: "Produtos" }, { n: 2, l: "Fornecedores" }, { n: 3, l: "Revisar" },
        ].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${step >= s.n ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-500"}`}>{s.n}</div>
            <div className={step >= s.n ? "font-medium" : "text-slate-400"}>{s.l}</div>
            {i < 2 && <div className="w-8 h-px bg-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* STEP 1 - PRODUTOS */}
      {step === 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-3">
              <Label>Título da cotação (opcional)</Label>
              <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex: Compra Julho 2026" data-testid="in-titulo" />
            </div>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input placeholder="Buscar produto..." value={buscaProd} onChange={(e) => setBuscaProd(e.target.value)} className="pl-9" data-testid="busca-prod-cot" />
            </div>
            <div className="text-xs text-slate-500 mb-2">{produtosSelArr.length} produtos selecionados de {produtos.length}</div>
            {produtos.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">
                Nenhum produto cadastrado.{" "}
                <button onClick={() => nav("/cotacoes/dashboard/produtos")} className="text-blue-700 hover:underline">Cadastrar agora</button>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y">
                {prodFiltered.map((p) => {
                  const sel = !!selProdutos[p.id];
                  return (
                    <div key={p.id} className="py-2 flex items-center gap-3" data-testid={`row-prod-${p.id}`}>
                      <Checkbox checked={sel} onCheckedChange={() => toggleProd(p.id)} data-testid={`chk-prod-${p.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.codigo && <span className="text-xs font-mono bg-slate-100 px-1 rounded mr-1">{p.codigo}</span>}{p.nome}</div>
                        <div className="text-xs text-slate-500">
                          {p.marca && <>Marca: {p.marca} · </>}{p.unidade && <>Un: {p.unidade}</>}
                        </div>
                      </div>
                      {sel && (
                        <div className="w-24">
                          <Input type="number" step="0.01" min="0" value={selProdutos[p.id]} onChange={(e) => changeQtd(p.id, e.target.value)} data-testid={`qtd-prod-${p.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button onClick={() => setStep(2)} disabled={produtosSelArr.length === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-step-2">
                Próximo <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 - FORNECEDORES */}
      {step === 2 && (
        <Card>
          <CardContent className="p-4">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input placeholder="Buscar fornecedor..." value={buscaForn} onChange={(e) => setBuscaForn(e.target.value)} className="pl-9" data-testid="busca-forn-cot" />
            </div>
            <div className="text-xs text-slate-500 mb-2">{fornecSelArr.length} fornecedores selecionados de {fornecedores.length}</div>
            {fornecedores.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">
                Nenhum fornecedor cadastrado.{" "}
                <button onClick={() => nav("/cotacoes/dashboard/fornecedores")} className="text-blue-700 hover:underline">Cadastrar agora</button>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y">
                {fornFiltered.map((f) => {
                  const sel = !!selFornec[f.id];
                  return (
                    <div key={f.id} className="py-2 flex items-center gap-3" data-testid={`row-forn-${f.id}`}>
                      <Checkbox checked={sel} onCheckedChange={() => toggleForn(f.id)} data-testid={`chk-forn-${f.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{f.razao_social}</div>
                        <div className="text-xs text-slate-500">
                          {f.nome_vendedor && <>{f.nome_vendedor} · </>}{f.celular || "sem celular"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
              <Button onClick={() => setStep(3)} disabled={fornecSelArr.length === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-step-3">
                Próximo <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 - REVISAR */}
      {step === 3 && (
        <Card>
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="font-semibold flex items-center gap-2 mb-2"><Package className="w-4 h-4" /> Produtos ({produtosSelArr.length})</div>
                <ul className="text-sm divide-y">
                  {produtosSelArr.map((pi) => {
                    const p = produtos.find((x) => x.id === pi.produto_id);
                    return <li key={pi.produto_id} className="py-1.5 flex justify-between"><span>{p?.codigo && <span className="text-xs font-mono bg-slate-100 px-1 rounded mr-1">{p.codigo}</span>}{p?.nome}</span><span className="text-slate-500">{pi.qtd} {p?.unidade || ""}</span></li>;
                  })}
                </ul>
              </div>
              <div>
                <div className="font-semibold flex items-center gap-2 mb-2"><Users className="w-4 h-4" /> Fornecedores ({fornecSelArr.length})</div>
                <ul className="text-sm divide-y">
                  {fornecSelArr.map((fi) => {
                    const f = fornecedores.find((x) => x.id === fi.fornecedor_id);
                    return <li key={fi.fornecedor_id} className="py-1.5"><div>{f?.razao_social}</div><div className="text-xs text-slate-500">{f?.nome_vendedor} · {f?.celular || "sem celular"}</div></li>;
                  })}
                </ul>
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
              <Button onClick={criar} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-criar-cotacao">
                {saving ? "Criando..." : <>Criar cotação <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
}
